import { Link } from "@remix-run/react";
import type { MetaFunction, LoaderFunctionArgs } from "@remix-run/cloudflare";
import { redirect } from "@remix-run/cloudflare";
import { Construction, Home, ArrowLeft, Star, Clock, User, MessageCircle } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { getCurrentLanguageFromPath } from "~/lib/language";

// 定义已存在的路由列表
const existingRoutes = [
  "/",
  "/about",
  "/wiki",
  "/wiki/knowledge-base/installation",
  "/wiki/knowledge-base",
  "/wiki/knowledge-base/file-security",
  "/wiki/faq",
  "/mods/eve2scarlet",
  "/mods/improved-perfect-defense", 
  "/mods/mod-hub",
  "/mods/ultimate-engine-tweaks",
  "/news/game",
  "/contact",
  "/disclaimer",
  "/privacy-policy",
  "/terms-of-use",
  "/Stellar-Blade-PC"
];

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const pathname = url.pathname;
  
  // 检查是否是语言特定的路径
  const currentLang = getCurrentLanguageFromPath(pathname);
  
  if (currentLang.code !== 'en') {
    // 获取基础路径（移除语言前缀）
    let basePath = pathname.replace(`/${currentLang.code}`, '') || '/';
    
    // 检查英文版本是否存在
    if (existingRoutes.includes(basePath)) {
      // 直接重定向到英文版本
      return redirect(basePath, 302);
    }
  }
  
  // 对于所有其他情况（包括不存在的路由），显示 Under Construction 页面
  return null;
}

export const meta: MetaFunction = () => {
  return [
    { title: "Under Construction | Stellar Blade Mods" },
    { 
      name: "description", 
      content: "This section is currently under construction. Discover amazing Stellar Blade mods on our homepage with character skins, gameplay enhancements, and more." 
    },
    { name: "robots", content: "noindex,follow" },
    { name: "keywords", content: "stellar blade mods, under construction, mod download, character mods, gameplay mods" },
    
    // Open Graph
    { property: "og:title", content: "Under Construction | Stellar Blade Mods" },
    { property: "og:description", content: "This section is currently under construction. Discover amazing Stellar Blade mods on our homepage." },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://stellarblademod.com/404" },
    
    // Twitter Card
    { name: "twitter:card", content: "summary" },
    { name: "twitter:title", content: "Under Construction | Stellar Blade Mods" },
    { name: "twitter:description", content: "This section is currently under construction. Discover amazing Stellar Blade mods on our homepage." },
    
    // Canonical URL
    { tagName: "link", rel: "canonical", href: "https://stellarblademod.com/404" }
  ];
};

export default function NotFound() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background/95 to-background/90">
      {/* Hero Section with Error Info */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary/10 via-primary/5 to-background">
        <div className="absolute inset-0 bg-grid-white/[0.02] bg-[size:60px_60px]" />
        <div className="relative">
          <div className="container mx-auto px-4 py-24 text-center">
            <div className="mx-auto max-w-3xl">
                             {/* Construction Icon */}
               <div className="mb-8 flex justify-center">
                 <div className="rounded-full bg-amber-500/10 p-8">
                   <Construction className="h-24 w-24 text-amber-500" />
                 </div>
               </div>
              
              {/* Error Title */}              
              <h1 className="mb-6 text-2xl font-semibold text-muted-foreground sm:text-3xl">
                Under Construction
              </h1>
              
              <p className="mb-8 text-lg text-muted-foreground max-w-2xl mx-auto">
                This section is currently under construction. We're working hard to bring you 
                amazing new features! In the meantime, explore our existing collection of 
                high-quality Stellar Blade mods.
              </p>
              
              {/* Action Button */}
              <div className="flex justify-center">
                <Button asChild size="lg" className="min-w-[200px]">
                  <Link to="/">
                    <Home className="mr-2 h-5 w-5" />
                    Back to Home
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Helpful Links Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="mx-auto max-w-4xl">
          <h3 className="mb-8 text-center text-2xl font-semibold">
            Popular Destinations
          </h3>
          
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {/* Featured Mods */}
            <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-primary/10 p-3 group-hover:bg-primary/20 transition-colors">
                    <Star className="h-6 w-6 text-primary" />
                  </div>
                </div>
                <h4 className="mb-2 font-semibold">Featured Mods</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Discover the most popular and highest-rated mods
                </p>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/#featured-mods">
                    View Featured
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Latest Mods */}
            <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-blue-500/10 p-3 group-hover:bg-blue-500/20 transition-colors">
                    <Clock className="h-6 w-6 text-blue-500" />
                  </div>
                </div>
                <h4 className="mb-2 font-semibold">Latest Releases</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Check out the newest mods from our community
                </p>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/#latest-mods">
                    View Latest
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Character Mods */}
            <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-purple-500/10 p-3 group-hover:bg-purple-500/20 transition-colors">
                    <User className="h-6 w-6 text-purple-500" />
                  </div>
                </div>
                <h4 className="mb-2 font-semibold">Character Mods</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Transform Eve with amazing character modifications
                </p>
                <Button variant="ghost" size="sm" asChild>
                  <Link to="/mods/eve2scarlet">
                    Browse Characters
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Community */}
            <Card className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
              <CardContent className="p-6 text-center">
                <div className="mb-4 flex justify-center">
                  <div className="rounded-full bg-green-500/10 p-3 group-hover:bg-green-500/20 transition-colors">
                    <MessageCircle className="h-6 w-6 text-green-500" />
                  </div>
                </div>
                <h4 className="mb-2 font-semibold">Community</h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Join our Discord community and connect with modders
                </p>
                <Button variant="ghost" size="sm" asChild>
                  <a 
                    href="https://discord.gg/daYR38qPB2" 
                    target="_blank" 
                    rel="noopener noreferrer"
                  >
                    Join Discord
                  </a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* SEO Content for Search Engines */}
      <div className="container mx-auto px-4 pb-16">
        <div className="mx-auto max-w-4xl text-center">
          <div className="rounded-lg bg-muted/50 p-8">
            <h3 className="mb-4 text-xl font-semibold">
              About Stellar Blade Mods
            </h3>
            <p className="text-muted-foreground leading-relaxed">
              Stellar Blade Mods is the premier destination for Stellar Blade modifications. 
              Our community creates and shares high-quality mods including character skins, 
              gameplay enhancements, visual improvements, and complete overhauls. Whether you're 
              looking for new outfits for Eve, improved combat mechanics, or stunning visual 
              effects, you'll find the best Stellar Blade mods right here. Join thousands of 
              players who have enhanced their gaming experience with our curated collection.
            </p>
            
            <div className="mt-6">
              <Button asChild>
                <Link to="/">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Explore Our Mods Collection
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 